import { useAuth0 } from "@auth0/auth0-react";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Button, Input, Modal } from "antd";
import { useSnackbar } from "notistack";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setAddCardModalOpen } from "../../store/common";
import { addPaymentMethods } from "../../utils/payment_methods";

const AddCardModal = observer(({ onCreate, makeDefaultPaymentMethod }) => {
  const stripe = useStripe();
  const dispatch = useDispatch();
  const elements = useElements();

  const { getIdTokenClaims } = useAuth0();
  const { enqueueSnackbar } = useSnackbar();
  const [makeDefault, setMakeDefault] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // State for name, email and postal code
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [postalCode, setPostalCode] = useState("");

  const addCardModalOpen = useSelector(
    (state) => state.commonReducer.addCardModalOpen
  );

  const handleSubmit = async (event) => {
    event.preventDefault();

    setIsSubmitting(true);

    if (!stripe || !elements) {
      console.log("Stripe.js has not yet loaded");
      setIsSubmitting(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
      billing_details: {
        name,
        email,
        address: {
          postal_code: postalCode,
        },
      },
    });

    if (error) {
      console.error(error);
      document.getElementById("card-errors").textContent = error.message;
      setIsSubmitting(false);
    } else {
      console.log("PaymentMethod:", paymentMethod.id);
      handlePaymentMethod(paymentMethod.id, name, email, postalCode);
      cardElement.clear();
      setEmail("");
      setName("");
      setPostalCode("");
    }
  };

  const handlePaymentMethod = async (paymentMethodId, name, email, postalCode) => {
    try {
      const token = await getIdTokenClaims();

      addPaymentMethods(token.__raw, paymentMethodId, makeDefault, postalCode)
        .then((res) => {
          if (res.data.success) {
            if (makeDefault === true) {
              makeDefaultPaymentMethod(paymentMethodId);
            }
            dispatch(setAddCardModalOpen(false));
            onCreate?.(paymentMethodId, res.data.message);
          } else {
            enqueueSnackbar(res.data.message, {
              variant: "error",
            });
            console.error(res.data.message);
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    } catch (e) {
      console.error(e);
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title={[<div className="text-2xl" key="title">Add Payment Method</div>]}
      centered
      open={addCardModalOpen}
      onCancel={() => dispatch(setAddCardModalOpen(false))}
      footer={null}
    >
      <form
        id="payment-form"
        onSubmit={(e) => {
          handleSubmit(e);
        }}
      >
        <div className="py-6 text-sm border-gray-300 w-full">
          {/* Name Input */}
          <div className="w-full mb-4">
            <label className="font-medium text-sm mb-2 block">Full Name</label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your full name"
              required
            />
          </div>

          {/* Email Input */}
          <div className="w-full mb-4">
            <label className="font-medium text-sm mb-2 block">Email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </div>

          {/* Postal Code Input */}
          <div className="w-full mb-4">
            <label className="font-medium text-sm mb-2 block">Postal Code / Zip Code</label>
            <Input
              type="text"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              placeholder="Enter postal code"
              required
            />
          </div>

          {/* Card Details */}
          <div className="w-full mb-6">
            <p className="font-medium text-sm mb-2">Card Details</p>
            <div id="card-element">
              <CardElement options={{ hidePostalCode: true }} />
            </div>
            <div id="card-errors" role="alert"></div>
          </div>

          {/* Default Payment Checkbox */}
          <label className="w-full flex items-center gap-3 mb-6">
            <input
              type="checkbox"
              value="1"
              onChange={(e) => setMakeDefault(e.target.checked)}
            />
            Make this as the default payment method
          </label>

          {/* Submit Button */}
          <div className="flex justify-end">
            <Button
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
              className="px-2 text-sm py-1.5 bg-blue-600 text-white rounded-lg"
            >
              Add Payment Method
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
});

export default AddCardModal;
